import { AppointmentStatus } from 'Turn/services/constants';
import { IAppointmentItemData } from 'types/appointment';
import { mixWithBlack, readableTextOn } from 'utils/color-contrast';

/**
 * Style A appointment cards — the per-shop calendar look gated by `shop.setting.appointmentStyleA`.
 *
 * Request/no-request/group each own a card color, a note becomes a chip, and check-in adds a corner
 * mark without touching the color. Check-in gets no hue of its own because blue means "group" here
 * and green means "no request" — both hues the old palette used for status are already spoken for.
 */

// Every ground is dark enough that `readableTextOn` picks WHITE, so all card labels match. That is
// the hard constraint, not the hue itself: a lighter green like #2FA84F reads 5.25:1 with dark ink
// but only 3.07:1 with white, so it would silently flip its label to black while the red and blue
// cards stayed white. The test asserts white wins on each one.
//
// These are the product-chosen muted tones. Red and green sit at 4.32:1 and 4.07:1 against white --
// below the 4.5:1 bar for body text, above the 3:1 bar for large text and UI components. Darkening
// them ~4-6% (#B85751, #4A825F) would clear 4.5:1 if that trade is ever revisited.
export const GROUP_BLUE = '#4A6FA5';        // white 5.11:1
export const REQUEST_RED = '#C05B54';       // white 4.32:1
export const NO_REQUEST_GREEN = '#4F8A65';  // white 4.07:1
export const NOTE_PURPLE = '#8558EC';       // white 4.56:1

/** How far the border/stripe is darkened from the ground so the card edge reads. */
export const EDGE_DARKEN = 0.25;

/**
 * A group booking is marked by a `Group` prefix on its note. Two shapes occur in real data and both
 * count, because nothing on the appointment record distinguishes a group otherwise — `group_id` is a
 * random UUID assigned to every appointment, group or not:
 *
 *   `Group: 2 Mani, Pedi`  generated by the BOOK GROUP flow (count after the colon)
 *   `Group 2: call first`  typed by staff (count before the colon)
 *
 * Tolerates leading whitespace, any casing, and spaces around the count. A colon is required, so
 * ordinary prose like "Group of 3" does not match.
 */
export const GROUP_NOTE_PATTERN = /^\s*group\s*\d*\s*:/i;

/** `Group 2: <note>` — a staff-typed marker whose remainder is a real note. */
const GROUP_MARKER_COUNTED = /^\s*group\s*\d+\s*:\s*/i;

export const isGroupNote = (note?: string | null): boolean => GROUP_NOTE_PATTERN.test(note || '');

/**
 * Separates the generated group marker from a note the user typed, in the note both share.
 *
 * A pipe rather than a dash: service names legitimately contain dashes ("Gel - Full Set"), so
 * splitting on " - " would cut inside the marker and mistake half a service name for the note.
 */
export const GROUP_NOTE_SEPARATOR = ' | ';

/**
 * The human note carried after the group marker, or null when the marker stands alone.
 *
 * Without this a group booking made with a real note ("allergic to acetone") would show nothing at
 * all on the card: the blue ground says "group", and the marker text itself is not worth a chip.
 */
export const getGroupNoteResidual = (note?: string | null): string | null => {
  const value = note || '';

  const at = value.indexOf(GROUP_NOTE_SEPARATOR);
  if (at !== -1) return value.slice(at + GROUP_NOTE_SEPARATOR.length).trim() || null;

  // `Group 2: call first` — everything after the colon is the note. Only when the count sits BEFORE
  // the colon: in the generated `Group: 2 Mani, Pedi` shape the remainder is the marker's own
  // service list, which the blue ground already conveys and which is not worth a chip.
  const counted = value.match(GROUP_MARKER_COUNTED);
  if (counted) return value.slice(counted[0].length).trim() || null;

  return null;
};

/**
 * Marker `Group...` dung truoc ghi chu cua nhan vien, da bo phan ngan cach.
 *
 * Cap voi `getGroupNoteResidual`: mot ben tra marker, mot ben tra ghi chu, de man chi tiet
 * chi cho sua ghi chu ma khong lam mat marker — thu duy nhat danh dau lich nhom.
 */
export const getGroupNoteMarker = (note?: string | null): string | null => {
  if (!isGroupNote(note)) return null;
  const value = note || '';

  const at = value.indexOf(GROUP_NOTE_SEPARATOR);
  if (at !== -1) return value.slice(0, at).trim();

  const counted = value.match(GROUP_MARKER_COUNTED);
  if (counted) return counted[0].trim();

  // Marker dung mot minh: ca chuoi la marker, chua co ghi chu nao di kem.
  return value.trim();
};

/**
 * Ghep marker voi ghi chu thanh note gui len server — phep nguoc cua cap tach o tren.
 *
 * `Group 2:` nhan vien tu go thi ghi chu di ngay sau dau hai cham, dung hinh dang cu.
 * Marker sinh tu dong (`Group: 2 Mani, Pedi`) phai dung dau ngan cach, neu khong ghi chu
 * se bi doc nham thanh phan duoi cua danh sach dich vu trong marker va bien mat khoi the.
 */
export const joinGroupNote = (marker: string | null | undefined, note?: string | null): string => {
  const typed = (note || '').trim();
  if (!marker) return typed;
  if (!typed) return marker;
  return GROUP_MARKER_COUNTED.test(marker)
    ? `${marker} ${typed}`
    : `${marker}${GROUP_NOTE_SEPARATOR}${typed}`;
};

export interface IAppointmentStyleA {
  /** Card background. */
  ground: string;
  /** Text color that stays readable on `ground`. */
  text: string;
  /** Darker shade of `ground` for the card border and left stripe, so the card edge stays visible. */
  edge: string;
  /** White heart badge — request-staff appointments only. */
  showHeart: boolean;
  /** Note text for the purple chip; null when there is no note, or the note is the group marker. */
  noteChip: string | null;
  /** Purple chip ground, paired with `noteChipText`. */
  noteChipBg: string;
  noteChipText: string;
  /** True once checked in — drawn as a mark in the card's bottom-right corner. */
  checkedIn: boolean;
}

/**
 * Maps an appointment to its style-A appearance.
 *
 * Precedence: group beats request for the GROUND, because a group booking is a different kind of
 * appointment while request/no-request is an attribute of an ordinary one. Request and no-request are
 * exhaustive, so every appointment gets exactly one ground.
 *
 * The heart is independent of the ground: a group booking can be per-person request, and the blue
 * ground cannot express that, so the badge stays on regardless of group.
 */
export const getAppointmentStyleA = (apt: IAppointmentItemData): IAppointmentStyleA => {
  const group = isGroupNote(apt.note);

  let ground = NO_REQUEST_GREEN;
  if (group) ground = GROUP_BLUE;
  else if (apt.requestStaff) ground = REQUEST_RED;

  // Check-in deliberately leaves the colour alone -- washing it out made red, green and blue hard to
  // tell apart at a glance. The corner mark is the only signal.
  const checkedIn = apt.status === AppointmentStatus.CHECK_IN;

  // The blue ground already says "group", so the generated marker itself is not worth a chip -- but
  // anything the user typed after it still is, or a real note would vanish from the card.
  const noteChip = group ? getGroupNoteResidual(apt.note) : (apt.note || null);

  return {
    ground,
    text: readableTextOn(ground),
    edge: mixWithBlack(ground, EDGE_DARKEN),
    showHeart: !!apt.requestStaff,
    noteChip,
    noteChipBg: NOTE_PURPLE,
    noteChipText: readableTextOn(NOTE_PURPLE),
    checkedIn,
  };
};
